import { INITIAL_STATE, PRODUCT_CATALOG_VERSION, PRODUCTS, LOCATIONS, BARTER_PRICES, TRAVEL_GEAR, NEIGHBORHOODS, STANDALONE_LOCATION_IDS, neighborhoodForLocation, travelClassFor, formatProductQuantity, productCapacityAmount, productQuantityForCapacity, inventoryCapacityUsed } from './constants.js';

const STORE_DEFAULTS = Object.freeze({
  corner_store_smokes_price: 8,
  corner_store_beer_price: 4,
  corner_store_scratcher_price: 3,
  corner_store_smokes_daily_limit: 10,
  corner_store_beer_daily_limit: 12,
  corner_store_scratcher_daily_limit: 20,
  smokes_resale_min: 6,
  smokes_resale_max: 18,
  pawn_skateboard_spawn_chance_low: 0.35,
  pawn_skateboard_spawn_chance_medium: 0.22,
  pawn_skateboard_spawn_chance_high: 0.12,
  pawn_bicycle_spawn_chance_low: 0.25,
  pawn_bicycle_spawn_chance_medium: 0.14,
  pawn_bicycle_spawn_chance_high: 0.07,
  pawn_skateboard_price_low: 125,
  pawn_skateboard_price_medium: 225,
  pawn_skateboard_price_high: 350,
  pawn_bicycle_price_low: 300,
  pawn_bicycle_price_medium: 500,
  pawn_bicycle_price_high: 800,
  skateboard_debt_growth_factor: 0.75,
  bicycle_debt_growth_factor: 0.55,
  skateboard_encounter_multiplier: 1.50,
  bicycle_encounter_multiplier: 1.25,
  skateboard_mugging_multiplier: 1.75,
  bicycle_mugging_multiplier: 1.40,
  local_travel_debt_factor_low: 0.10,
  local_travel_debt_factor_medium: 0.20,
  local_travel_debt_factor_high: 0.30,
  travel_risk_multiplier_low: 0.50,
  travel_risk_multiplier_medium: 0.75,
  travel_risk_multiplier_high: 1.00,
  travel_consequence_multiplier_low: 0.60,
  travel_consequence_multiplier_medium: 0.80,
  travel_consequence_multiplier_high: 1.00,
  travel_micro_chance_low: 0.30,
  travel_micro_chance_medium: 0.24,
  travel_micro_chance_high: 0.18,
  illustrated_mugger_replacement_chance: 0.55,
  ken_rep_required_low: 40, ken_rep_required_medium: 55, ken_rep_required_high: 70,
  ken_meth_price_factor_low: 0.55, ken_meth_price_factor_medium: 0.65, ken_meth_price_factor_high: 0.75,
  ken_meth_stock_low: 60, ken_meth_stock_medium: 40, ken_meth_stock_high: 25,
  ken_weed_demand_low: 56, ken_weed_demand_medium: 42, ken_weed_demand_high: 28,
  ken_weed_offer_factor_low: 1.20, ken_weed_offer_factor_medium: 1.10, ken_weed_offer_factor_high: 1.00,
  ken_junk_value_factor_low: 0.90, ken_junk_value_factor_medium: 0.80, ken_junk_value_factor_high: 0.70,
  ken_rep_price_effect: 0.20, ken_rep_value_effect: 0.35,
});

const SCRATCHER_PRIZES = Object.freeze([
  [1, 0.26], [2, 0.13], [5, 0.06], [10, 0.03], [25, 0.012],
  [50, 0.005], [100, 0.002], [500, 0.0008], [2000, 0.0002],
]);

const storeRules = (state) => ({ ...STORE_DEFAULTS, ...(state.serverRules || {}) });

const scratcherReveal = (prize) => {
  if (prize >= 2000) return 'JACKPOT — the cashier checks the ticket twice and looks personally offended.';
  if (prize >= 500) return 'BIG WINNER — suddenly everybody near the counter is your cousin.';
  if (prize >= 100) return 'A real winner. The silver dust finally did something useful.';
  if (prize >= 25) return 'Not bad. Enough to make buying another ticket feel dangerously reasonable.';
  if (prize > 0) return 'Winner — technically. Try not to spend it all in one parking lot.';
  return 'Loser — three useless symbols and enough silver dust to fingerprint a raccoon.';
};

const emptyStoreStats = () => ({ visits: 0, smokesPurchased: 0, beerPurchased: 0, scratchersPurchased: 0, scratcherSpent: 0, scratcherPaid: 0, jackpotCount: 0, smokesResold: 0, smokesResaleRevenue: 0, smokesBuyerDeals: 0, beersDrank: 0, boardViews: 0 });
const bulletinBoard = (day) => ({
  version: 2, title: 'The Corner Store Bulletin Board', viewedOnDay: Number(day || 1),
  subtitle: 'System notices now. Jobs, rumors, graffiti, and moderated player requests later.',
  categories: ['system', 'update', 'maintenance', 'event', 'warning', 'rumor', 'jobs', 'graffiti', 'requests'],
  posts: [
    { id: 'system-board-online', sourceKey: 'system-board-online', category: 'system', title: 'THE BOARD IS ONLINE', body: 'Official game notices, maintenance messages, and new-feature announcements are posted here.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.7-alpha1-chaz', sourceKey: 'release-0.0.7-alpha1-chaz', category: 'update', title: 'SOMETHING LOUD IS CIRCLING THE TRAILER PARK', body: 'Chaz has his dad’s T-Bird, a Winger cassette, and absolutely no adult supervision. One ounce of bud may get you a block of rides.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.7-alpha1-trailer-girl', sourceKey: 'release-0.0.7-alpha1-trailer-girl', category: 'update', title: 'NEIGHBORHOOD HEALTH SERVICES EXPANDED', body: 'A Trailer Park resident is offering questionable personal care, warm beer, and crushed cigarettes.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.8-alpha1-board', sourceKey: 'release-0.0.8-alpha1-board', category: 'update', title: 'THE BOARD GOT A FACELIFT', body: 'The Corner Store board now has real pinned notes. Tap one to read it without squinting through years of grease.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.10-alpha1-travel-gear', sourceKey: 'release-0.0.10-alpha1-travel-gear', category: 'update', title: 'QUESTIONABLE TRANSPORTATION ARRIVED', body: 'The Pawn Shop may stock a skateboard or bicycle. They slow debt growth and make street trouble notice you faster.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.11-alpha1-roadside-ken', sourceKey: 'release-0.0.11-alpha1-roadside-ken', category: 'update', title: 'THE BOONIES ARE OPEN FOR BUSINESS', body: 'Roadside incidents are busier, lucky trash is appearing, and Ken is trading meth for weed and electronics.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.12-alpha1-neighborhoods', sourceKey: 'release-0.0.12-alpha1-neighborhoods', category: 'update', title: 'THE MAP GOT NEIGHBORHOODS', body: 'Mt. Gate, Redding, and Arcata now organize local stops. Crossing town costs real time; moving around the neighborhood costs less.', author: 'The Dope Man System', pinned: true },
    { id: 'release-0.0.14-alpha1-pawn-shop', sourceKey: 'release-0.0.14-alpha1-pawn-shop', category: 'update', title: 'THE PAWN SHOP GOT DANGEROUS', body: 'Wally cleaned the counter just enough to reveal the grime underneath. Watch the parking lot; a knife-waving junkie has been taxing arrivals.', author: 'The Dope Man System', pinned: true },
    { id: 'future-local-jobs', category: 'jobs', title: 'LOCAL JOBS — COMING SOON', body: 'The job section is still being stapled together.', pinned: false },
    { id: 'future-player-requests', category: 'requests', title: 'PLAYER REQUESTS — COMING SOON', body: 'Moderated player notices will arrive in a future build.', pinned: false },
  ],
});

export const generateStreetPrices = (locationId, stateOrRules = {}, day = 1) => {
  const location = LOCATIONS.find((item) => item.id === locationId);
  if (!location) throw new Error('That location does not exist.');
  if (location.isHome || location.isPawnShop || location.isSceneLocation) return { smokes: null };
  const rules = { ...STORE_DEFAULTS, ...(stateOrRules.serverRules || stateOrRules || {}) };
  let minimum = Number(rules.smokes_resale_min);
  let maximum = Number(rules.smokes_resale_max);
  if (maximum < minimum) [minimum, maximum] = [maximum, minimum];
  minimum = Math.max(1, Math.floor(minimum * Number(location.priceMultiplier || 1)));
  maximum = Math.max(minimum, Math.floor(maximum * Number(location.priceMultiplier || 1)));
  const span = maximum - minimum + 1;
  const stableSeed = [...locationId].reduce((total, character) => total + character.charCodeAt(0), 0) + Number(day) * 17;
  return { smokes: minimum + (stableSeed % span) };
};

const ensureCornerStoreState = (state) => {
  state.productCatalogVersion ??= 1;
  state.inventory ||= {};
  state.stash ||= {};
  PRODUCTS.forEach((product) => {
    state.inventory[product.id] = Math.max(0, Number(state.inventory[product.id] || 0));
    state.stash[product.id] = Math.max(0, Number(state.stash[product.id] || 0));
  });
  state.productCatalog ||= {
    version: Number(state.productCatalogVersion || 1), latestVersion: PRODUCT_CATALOG_VERSION,
    products: PRODUCTS.map((product) => ({ id: product.id, name: product.name, unitSingular: product.unitSingular, unitPlural: product.unitPlural, unitShort: product.unitShort, capacityWeight: product.capacityWeight, active: Number(state.productCatalogVersion || 1) >= PRODUCT_CATALOG_VERSION || !['acid','coke'].includes(product.id), options: product.options.map((option) => ({ label: option.label, quantity: option.weight })) })),
  };
  state.streetInventory ||= { smokes: 0, beer: 0 };
  state.streetInventory.smokes = Math.max(0, Number.parseInt(state.streetInventory.smokes || 0, 10));
  state.streetInventory.beer = Math.max(0, Number.parseInt(state.streetInventory.beer || 0, 10));
  if (!state.streetPrices || !Object.hasOwn(state.streetPrices, 'smokes')) state.streetPrices = generateStreetPrices(state.currentLocationId, state, state.day);
  state.cornerStore ||= {};
  const priorStats = { ...emptyStoreStats(), ...(state.cornerStore.stats || {}) };
  const priorHistory = Array.isArray(state.cornerStore.scratcherHistory) ? state.cornerStore.scratcherHistory.slice(-50) : [];
  if (Number(state.cornerStore.day ?? state.day) !== Number(state.day)) state.cornerStore = { stats: priorStats, scratcherHistory: priorHistory };
  Object.assign(state.cornerStore, {
    day: Number(state.cornerStore.day ?? state.day), smokesBought: Number(state.cornerStore.smokesBought || 0),
    beerBought: Number(state.cornerStore.beerBought || 0), scratchersBought: Number(state.cornerStore.scratchersBought || 0),
    lastScratcherResults: Array.isArray(state.cornerStore.lastScratcherResults) ? state.cornerStore.lastScratcherResults : [],
    lastScratcherReveals: Array.isArray(state.cornerStore.lastScratcherReveals) ? state.cornerStore.lastScratcherReveals : [],
    lastScratcherCost: Number(state.cornerStore.lastScratcherCost || 0), lastScratcherPayout: Number(state.cornerStore.lastScratcherPayout || 0),
    scratcherHistory: Array.isArray(state.cornerStore.scratcherHistory) ? state.cornerStore.scratcherHistory.slice(-50) : priorHistory,
    stats: { ...emptyStoreStats(), ...(state.cornerStore.stats || priorStats) },
  });
  state.bulletinBoard ||= bulletinBoard(state.day);
  state.barterInventory ||= {};
  Object.keys(BARTER_PRICES).forEach((itemId) => {
    state.barterInventory[itemId] = Math.max(0, Number.parseInt(state.barterInventory[itemId] || 0, 10));
  });
  state.marketNotebook ||= {};
  state.marketPriceLabels ||= { buy: {}, sell: {} };
  state.marketPriceLabels.buy ||= {};
  state.marketPriceLabels.sell ||= {};
  state.eventRuntime ||= { counts: {}, last_day: {}, flags: [], counters: {} };
  state.eventRuntime.counters ||= {};
  state.travelServices ||= {};
  state.travelServices.chaz ||= {};
  const chaz = state.travelServices.chaz;
  chaz.ridesRemaining = Math.max(0, Number.parseInt(chaz.ridesRemaining || 0, 10));
  chaz.active = Boolean(chaz.active) && chaz.ridesRemaining > 0;
  chaz.ridesGrantedThisDeal = Math.max(0, Number.parseInt(chaz.ridesGrantedThisDeal || 0, 10));
  chaz.startedDay = Math.max(0, Number.parseInt(chaz.startedDay || 0, 10));
  chaz.policeRiskMultiplier = Number(chaz.policeRiskMultiplier || 1.75);
  chaz.waiveTravelDebt = chaz.waiveTravelDebt !== false;
  chaz.label ||= 'Chaz and the T-Bird';
  chaz.stats = { dealsAccepted: 0, ridesGranted: 0, ridesUsed: 0, policeStops: 0, weedPaidGrams: 0, ...(chaz.stats || {}) };
  state.travelGear ||= {};
  Object.keys(TRAVEL_GEAR).forEach((itemId) => {
    state.travelGear[itemId] ||= {};
    state.travelGear[itemId].owned = Boolean(state.travelGear[itemId].owned);
    state.travelGear[itemId].acquiredDay = Number(state.travelGear[itemId].acquiredDay || 0);
    state.travelGear[itemId].purchasePrice = Number(state.travelGear[itemId].purchasePrice || 0);
  });
  state.pawnTravelStock ||= { day: 0 };
  Object.keys(TRAVEL_GEAR).forEach((itemId) => {
    state.pawnTravelStock[itemId] ||= { available: false, price: 0 };
    state.pawnTravelStock[itemId].available = Boolean(state.pawnTravelStock[itemId].available);
    state.pawnTravelStock[itemId].price = Number(state.pawnTravelStock[itemId].price || 0);
  });
  state.kenHouse ||= {};
  const ken = state.kenHouse;
  const difficulty = String(state.difficulty || 'high');
  const rules = storeRules(state);
  const rep = Math.max(0, Math.min(100, Number(state.streetRep || 50)));
  const centered = (rep - 50) / 50;
  const priceEffect = Number(rules.ken_rep_price_effect);
  const valueEffect = Number(rules.ken_rep_value_effect);
  ken.day = Number(ken.day || state.day);
  ken.repRequirement = Number(rules[`ken_rep_required_${difficulty}`]);
  ken.repDiscount = Math.max(-priceEffect, Math.min(priceEffect, centered * priceEffect));
  ken.repValueBonus = Math.max(-valueEffect, Math.min(valueEffect, centered * valueEffect));
  ken.baseMethPrice = Math.max(20, Math.floor(100 * Number(rules[`ken_meth_price_factor_${difficulty}`])));
  ken.baseWeedOffer = Math.max(1, Math.floor(10 * Number(rules[`ken_weed_offer_factor_${difficulty}`])));
  ken.baseJunkFactor = Math.max(0.25, Number(rules[`ken_junk_value_factor_${difficulty}`]));
  ken.methPriceNoise = Number(ken.methPriceNoise || 1);
  ken.weedOfferNoise = Number(ken.weedOfferNoise || 1);
  ken.methPrice = Math.max(20, Math.floor(ken.baseMethPrice * ken.methPriceNoise * (1 - ken.repDiscount)));
  ken.weedOffer = Math.max(1, Math.floor(ken.baseWeedOffer * ken.weedOfferNoise * (1 + ken.repValueBonus)));
  ken.junkFactor = Math.max(0.25, ken.baseJunkFactor * (1 + ken.repValueBonus));
  ken.methStock = Number(ken.methStock || rules[`ken_meth_stock_${difficulty}`]);
  ken.weedDemand = Number(ken.weedDemand || rules[`ken_weed_demand_${difficulty}`]);
  ken.wifeFlavor ||= "Somebody inside is yelling about a missing bottle opener.";
  ken.stats ||= {};
  state.lastTravel ||= { mode: 'normal', travelClass: 'unknown', dayAdvanced: true, policeRiskMultiplier: 1, encounterMultiplier: 1, muggingMultiplier: 1, debtGrowthFactor: 1, serviceEnded: false };
  state.worldMap = { neighborhoods: NEIGHBORHOODS, currentNeighborhoodId: neighborhoodForLocation(state.currentLocationId)?.id || null, standaloneLocationIds: STANDALONE_LOCATION_IDS };
  state.dailyLocationMarkets ||= { day: Number(state.day || 1), productCatalogVersion: Number(state.productCatalogVersion || 1), markets: {}, sellMarkets: {}, stocks: {}, demands: {}, streetPrices: {}, events: {} };
  if (Number(state.dailyLocationMarkets.day || 0) !== Number(state.day || 1) || Number(state.dailyLocationMarkets.productCatalogVersion ?? state.productCatalogVersion) !== Number(state.productCatalogVersion || 1)) {
    state.dailyLocationMarkets = { day: Number(state.day || 1), productCatalogVersion: Number(state.productCatalogVersion || 1), markets: {}, sellMarkets: {}, stocks: {}, demands: {}, streetPrices: {}, events: {} };
  }
  state.dailyLocationMarkets.productCatalogVersion ??= Number(state.productCatalogVersion || 1);
  for (const key of ['markets', 'sellMarkets', 'stocks', 'demands', 'streetPrices', 'events']) state.dailyLocationMarkets[key] ||= {};
  const legacyMarketPrices = state.marketPrices || {};
  if (!state.marketBuyPrices || Object.keys(state.marketBuyPrices).length === 0) state.marketBuyPrices = { ...legacyMarketPrices };
  if (!state.marketSellPrices || Object.keys(state.marketSellPrices).length === 0) state.marketSellPrices = { ...legacyMarketPrices };
  state.marketStock ||= {};
  state.marketDemand ||= {};
  state.marketProfile ||= {};
  return state;
};

const rollPawnTravelStock = (state, random = Math.random) => {
  ensureCornerStoreState(state);
  const day = Number(state.day || 1);
  if (Number(state.pawnTravelStock.day || 0) === day) return state.pawnTravelStock;
  const rules = storeRules(state);
  const difficulty = String(state.difficulty || 'high').toLowerCase();
  state.pawnTravelStock = { day };
  Object.keys(TRAVEL_GEAR).forEach((itemId) => {
    const chance = Number(rules[`pawn_${itemId}_spawn_chance_${difficulty}`]);
    const price = Number(rules[`pawn_${itemId}_price_${difficulty}`]);
    state.pawnTravelStock[itemId] = { available: !state.travelGear[itemId].owned && random() < chance, price };
  });
  return state.pawnTravelStock;
};

const travelPolicy = (state, mode) => {
  const rules = storeRules(state);
  if (mode === 'normal') return { debtFactor: 1, encounterMultiplier: 1, muggingMultiplier: 1, policeMultiplier: 1 };
  if (mode === 'chaz') {
    const chaz = state.travelServices.chaz;
    if (!chaz.active || chaz.ridesRemaining <= 0) throw new Error('Chaz is not available for another ride.');
    return { debtFactor: chaz.waiveTravelDebt ? 0 : 1, encounterMultiplier: 1, muggingMultiplier: 1, policeMultiplier: Number(chaz.policeRiskMultiplier || 1.75) };
  }
  if (!Object.hasOwn(TRAVEL_GEAR, mode)) throw new Error('Travel mode must be normal, chaz, skateboard, or bicycle.');
  if (!state.travelGear[mode]?.owned) throw new Error(`You do not own a ${mode}.`);
  return {
    debtFactor: Number(rules[`${mode}_debt_growth_factor`] ?? TRAVEL_GEAR[mode].debtGrowthFactor),
    encounterMultiplier: Number(rules[`${mode}_encounter_multiplier`] ?? TRAVEL_GEAR[mode].encounterMultiplier),
    muggingMultiplier: Number(rules[`${mode}_mugging_multiplier`] ?? TRAVEL_GEAR[mode].muggingMultiplier),
    policeMultiplier: 1,
  };
};

const positiveQuantity = (value, maximum = 1000) => {
  const quantity = Number.parseInt(value, 10);
  if (!Number.isFinite(quantity) || quantity <= 0 || quantity > maximum) {
    throw new Error(`Quantity must be between 1 and ${maximum}.`);
  }
  return quantity;
};

const rollScratcher = (random) => {
  const roll = random();
  let cumulative = 0;
  for (const [prize, chance] of SCRATCHER_PRIZES) {
    cumulative += chance;
    if (roll < cumulative) return prize;
  }
  return 0;
};

const marketProducts = (location, side) => side === 'buy'
  ? (location.buyProducts || location.availableProducts || [])
  : (location.sellProducts || location.availableProducts || []);

const rollRange = (range, random) => {
  if (!Array.isArray(range) || range.length !== 2) return null;
  const min = Math.max(0, Number(range[0]));
  const max = Math.max(min, Number(range[1]));
  return Math.floor(random() * (max - min + 1)) + min;
};

const rollMultiplierRange = (range, random) => {
  if (!Array.isArray(range) || range.length !== 2) return null;
  const min = Math.max(0, Number(range[0]));
  const max = Math.max(min, Number(range[1]));
  return min + random() * (max - min);
};

const rollProductPrice = (product, location, multiplier, random, side = 'buy') => {
  const explicit = (side === 'buy' ? location.buyPriceRanges : location.sellPriceRanges)?.[product.id];
  if (explicit) return rollRange(explicit, random);
  let min = product.baseMin;
  let max = product.baseMax;
  if (location.specialties?.includes(product.id)) {
    min = Math.floor(min * 0.5);
    max = Math.floor(max * 0.8);
  }
  min = Math.max(1, Math.floor(min * Number(multiplier || 1)));
  max = Math.max(min, Math.floor(max * Number(multiplier || 1)));
  return Math.floor(random() * (max - min + 1)) + min;
};

const productVolatility = (product, location, random) => {
  const rules = { ...(product.volatility || {}), ...(location.productVolatility?.[product.id] || {}) };
  if (!Object.keys(rules).length) return { multiplier: 1, event: null };
  const roll = random();
  const crashChance = Math.max(0, Math.min(1, Number(rules.crashChance || 0)));
  const spikeChance = Math.max(0, Math.min(1 - crashChance, Number(rules.spikeChance || 0)));
  if (roll < crashChance) {
    const min = Number(rules.crashMin ?? .5); const max = Math.max(min, Number(rules.crashMax ?? .75));
    return { multiplier: min + random() * (max - min), event: 'crash' };
  }
  if (roll < crashChance + spikeChance) {
    const min = Number(rules.spikeMin ?? 1.5); const max = Math.max(min, Number(rules.spikeMax ?? 2.2));
    return { multiplier: min + random() * (max - min), event: 'spike' };
  }
  return { multiplier: 1, event: null };
};

const boundedProductPrice = (product, price) => {
  let bounded = Math.max(1, Math.floor(Number(price || 0)));
  if (product.priceFloor != null) bounded = Math.max(bounded, Number(product.priceFloor));
  if (product.priceCap != null) bounded = Math.min(bounded, Number(product.priceCap));
  return Math.floor(bounded);
};

const activeProductsForVersion = (version) => Number(version || 1) >= PRODUCT_CATALOG_VERSION ? PRODUCTS : PRODUCTS.filter((product) => !['acid', 'coke'].includes(product.id));

export const generateMarket = (locationId, random = Math.random, productIds = null) => {
  const location = LOCATIONS.find((item) => item.id === locationId);
  if (!location) throw new Error('That location does not exist.');
  const activeProducts = productIds ? PRODUCTS.filter((product) => productIds.includes(product.id)) : PRODUCTS;
  const buyPrices = Object.fromEntries(activeProducts.map((product) => [product.id, null]));
  const sellPrices = Object.fromEntries(activeProducts.map((product) => [product.id, null]));
  const stock = Object.fromEntries(activeProducts.map((product) => [product.id, null]));
  const demand = Object.fromEntries(activeProducts.map((product) => [product.id, null]));
  const events = {};
  if (location.isHome || location.isPawnShop || location.isSceneLocation) return { buyPrices: {}, sellPrices: {}, stock: {}, demand: {}, events: {} };
  const buyProducts = marketProducts(location, 'buy');
  const sellProducts = marketProducts(location, 'sell');
  activeProducts.forEach((product) => {
    if (buyProducts.includes(product.id)) {
      const rangedMultiplier = rollMultiplierRange(location.buyPriceMultiplierRanges?.[product.id], random);
      const multiplier = rangedMultiplier ?? location.buyPriceMultipliers?.[product.id] ?? location.priceMultiplier ?? 1;
      buyPrices[product.id] = rollProductPrice(product, location, multiplier, random, 'buy');
    }
    if (sellProducts.includes(product.id)) {
      const hasSellOverride = location.sellPriceMultipliers?.[product.id] != null || location.sellPriceRanges?.[product.id] != null || location.sellPriceMultiplierRanges?.[product.id] != null;
      if (buyProducts.includes(product.id) && !hasSellOverride) {
        sellPrices[product.id] = buyPrices[product.id];
      } else {
        const rangedMultiplier = rollMultiplierRange(location.sellPriceMultiplierRanges?.[product.id], random);
        const multiplier = rangedMultiplier ?? location.sellPriceMultipliers?.[product.id] ?? location.priceMultiplier ?? 1;
        sellPrices[product.id] = rollProductPrice(product, location, multiplier, random, 'sell');
      }
    }
    if (buyPrices[product.id] != null || sellPrices[product.id] != null) {
      const volatility = productVolatility(product, location, random);
      if (volatility.event) {
        events[product.id] = volatility.event;
        if (buyPrices[product.id] != null) buyPrices[product.id] = Math.max(1, Math.floor(buyPrices[product.id] * volatility.multiplier));
        if (sellPrices[product.id] != null) sellPrices[product.id] = Math.max(1, Math.floor(sellPrices[product.id] * volatility.multiplier));
      }
    }
    if (buyPrices[product.id] != null) buyPrices[product.id] = boundedProductPrice(product, buyPrices[product.id]);
    if (sellPrices[product.id] != null) sellPrices[product.id] = boundedProductPrice(product, sellPrices[product.id]);
    if (buyPrices[product.id] != null && sellPrices[product.id] != null) {
      sellPrices[product.id] = Math.min(sellPrices[product.id], Math.max(1, buyPrices[product.id] - 1));
    }
    if (location.dailyStock?.[product.id] != null) stock[product.id] = Math.max(0, Number(location.dailyStock[product.id]));
    if (location.dailyStockRanges?.[product.id]) stock[product.id] = rollRange(location.dailyStockRanges[product.id], random);
    const stockoutChance = Math.max(0, Math.min(1, Number(location.dailyStockoutChances?.[product.id] || 0)));
    if (stock[product.id] != null && stockoutChance > 0 && random() < stockoutChance) stock[product.id] = 0;
    if (location.dailyDemand?.[product.id] != null) demand[product.id] = Math.max(0, Number(location.dailyDemand[product.id]));
    if (location.dailyDemandRanges?.[product.id]) demand[product.id] = rollRange(location.dailyDemandRanges[product.id], random);
  });
  return { buyPrices, sellPrices, stock, demand, events };
};


export const generatePrices = (locationId, random = Math.random) => generateMarket(locationId, random).buyPrices;

export const handleTravel = (intendedDestinationId, currentState, random = Math.random, travelMode = 'normal') => {
  let destinationId = intendedDestinationId;
  const newState = ensureCornerStoreState({
    ...currentState,
    inventory: { ...currentState.inventory },
    stash: { ...currentState.stash },
    streetInventory: { ...(currentState.streetInventory || {}) },
    cornerStore: { ...(currentState.cornerStore || {}) },
    travelGear: structuredClone(currentState.travelGear || {}),
    pawnTravelStock: structuredClone(currentState.pawnTravelStock || {}),
    kenHouse: structuredClone(currentState.kenHouse || {}),
    travelServices: structuredClone(currentState.travelServices || {}),
    dailyLocationMarkets: structuredClone(currentState.dailyLocationMarkets || {}),
    messages: [],
  });

  if (currentState.currentLocationId === 'corner_store') throw new Error('Use the store exit before traveling anywhere else.');
  if (intendedDestinationId === 'corner_store') {
    if (currentState.currentLocationId !== 'trailer_park') throw new Error('The Corner Store is a local stop reached from the Trailer Park.');
    newState.currentLocationId = 'corner_store';
    newState.activeEvent = null;
    newState.eventQueue = [];
    newState.messages = ['Walked over to The Corner Store. The day and your debt did not move an inch.'];
    newState.cornerStore.stats.visits += 1;
    newState.lastTravel = { mode: 'walk', travelClass: 'special_local', dayAdvanced: false, debtGrowthFactor: 0, serviceEnded: false };
    newState.worldMap.currentNeighborhoodId = 'mt_gate';
    return newState;
  }

  const intendedDest = LOCATIONS.find((location) => location.id === intendedDestinationId);
  if (!intendedDest || intendedDest.isHidden) throw new Error('That destination is unavailable.');
  if (intendedDestinationId === currentState.currentLocationId) throw new Error('You are already at that location.');

  const travelClass = travelClassFor(currentState.currentLocationId, intendedDestinationId);
  const local = travelClass === 'local';
  const mode = String(travelMode || 'normal').toLowerCase();
  if (local && mode !== 'normal') throw new Error('Local neighborhood trips do not use Chaz or travel gear.');
  const rules = storeRules(newState);
  const localFactor = Number(rules[`local_travel_debt_factor_${newState.difficulty}`] ?? 0.30);
  const policy = local ? { debtFactor: localFactor, encounterMultiplier: 1, muggingMultiplier: 1, policeMultiplier: 1 } : travelPolicy(newState, mode);
  const chaz = newState.travelServices.chaz;
  const useChaz = !local && mode === 'chaz';

  if (!local && mode === 'normal' && random() < 0.05 && intendedDestinationId !== 'cavemans_house' && intendedDestinationId !== 'home') {
    destinationId = 'cavemans_house';
    newState.messages = ["You got completely lost on the backroads and ended up at Caveman's house in Lakehead!"];
  } else if (local) {
    const neighborhood = neighborhoodForLocation(intendedDestinationId);
    newState.messages = [`Moved around ${neighborhood?.name || 'the neighborhood'} to ${intendedDest.name} without burning a full day.`];
  } else {
    newState.messages = [useChaz ? `Chaz fires up the T-Bird and drives you to ${intendedDest.name} for free.` : mode === 'skateboard' ? `Rattled toward ${intendedDest.name} on your beat-up skateboard.` : mode === 'bicycle' ? `Pedaled toward ${intendedDest.name} on your used bicycle.` : `Traveled to ${intendedDest.name}.`];
  }

  const destination = LOCATIONS.find((location) => location.id === destinationId);
  if (!local) newState.day += 1;
  newState.currentLocationId = destinationId;
  const debtMultiplier = Number(newState.serverRules?.travel_debt_multiplier ?? 1.10);
  const normalDebt = Math.floor(newState.debt * debtMultiplier);
  const normalGrowth = Math.max(0, normalDebt - newState.debt);
  newState.debt += Math.floor(normalGrowth * policy.debtFactor);

  let serviceEnded = false;
  if (useChaz) {
    chaz.ridesRemaining = Math.max(0, chaz.ridesRemaining - 1);
    chaz.stats.ridesUsed = Number(chaz.stats.ridesUsed || 0) + 1;
    serviceEnded = chaz.ridesRemaining <= 0;
    if (serviceEnded) chaz.active = false;
    newState.messages.push(`Chaz rides remaining: ${chaz.ridesRemaining}.`);
    if (serviceEnded) newState.messages.push('Chaz pulls onto somebody’s lawn and admits he is way too stoned to keep driving.');
  }
  const productRisk = carriedProductRisk(newState);
  newState.lastTravel = {
    mode: local ? 'normal' : mode,
    travelClass,
    dayAdvanced: !local,
    currentNeighborhoodId: neighborhoodForLocation(currentState.currentLocationId)?.id || null,
    destinationNeighborhoodId: neighborhoodForLocation(destinationId)?.id || null,
    policeRiskMultiplier: policy.policeMultiplier * productRisk.police,
    encounterMultiplier: policy.encounterMultiplier,
    muggingMultiplier: policy.muggingMultiplier * productRisk.mugging,
    productPoliceMultiplier: productRisk.police,
    productMuggingMultiplier: productRisk.mugging,
    productRiskTiers: productRisk.tiers,
    debtGrowthFactor: policy.debtFactor,
    serviceEnded,
  };

  if (!local) {
    newState.productCatalogVersion = PRODUCT_CATALOG_VERSION;
    if (newState.productCatalog) newState.productCatalog.version = PRODUCT_CATALOG_VERSION;
  }
  if (!local || Number(newState.dailyLocationMarkets.day || 0) !== Number(newState.day) || Number(newState.dailyLocationMarkets.productCatalogVersion ?? newState.productCatalogVersion) !== Number(newState.productCatalogVersion)) {
    newState.dailyLocationMarkets = { day: Number(newState.day), productCatalogVersion: Number(newState.productCatalogVersion || 1), markets: {}, sellMarkets: {}, stocks: {}, demands: {}, streetPrices: {}, events: {} };
  }
  const cache = newState.dailyLocationMarkets;
  for (const key of ['markets', 'sellMarkets', 'stocks', 'demands', 'streetPrices', 'events']) cache[key] ||= {};
  if (!cache.markets[destinationId]) {
    const generated = generateMarket(destinationId, random, activeProductsForVersion(newState.productCatalogVersion).map((product) => product.id));
    cache.markets[destinationId] = generated.buyPrices;
    cache.sellMarkets[destinationId] = generated.sellPrices;
    cache.stocks[destinationId] = generated.stock;
    cache.demands[destinationId] = generated.demand;
    cache.events[destinationId] = generated.events || {};
  }
  cache.sellMarkets[destinationId] ||= structuredClone(cache.markets[destinationId]);
  cache.stocks[destinationId] ||= Object.fromEntries(PRODUCTS.map((product) => [product.id, null]));
  cache.demands[destinationId] ||= Object.fromEntries(activeProductsForVersion(newState.productCatalogVersion).map((product) => [product.id, null]));
  cache.events[destinationId] ||= {};
  cache.streetPrices[destinationId] ||= generateStreetPrices(destinationId, newState, newState.day);
  newState.marketPrices = structuredClone(cache.markets[destinationId]);
  newState.marketBuyPrices = structuredClone(cache.markets[destinationId]);
  newState.marketSellPrices = structuredClone(cache.sellMarkets[destinationId]);
  newState.marketStock = structuredClone(cache.stocks[destinationId]);
  newState.marketDemand = structuredClone(cache.demands[destinationId]);
  newState.marketProfile = {
    locationId: destinationId,
    buyProducts: [...marketProducts(destination, 'buy')],
    sellProducts: [...marketProducts(destination, 'sell')],
    limitedStock: Object.values(newState.marketStock).some((value) => value != null),
    limitedDemand: Object.values(newState.marketDemand).some((value) => value != null),
    volatilityEvents: structuredClone(cache.events[destinationId] || {}),
  };
  newState.streetPrices = structuredClone(cache.streetPrices[destinationId]);
  newState.worldMap = { neighborhoods: NEIGHBORHOODS, currentNeighborhoodId: neighborhoodForLocation(destinationId)?.id || null, standaloneLocationIds: STANDALONE_LOCATION_IDS };

  if (destinationId === 'kens_house') {
    if (!newState.kenHouse.rolled || Number(newState.kenHouse.day || 0) !== Number(newState.day)) {
      newState.kenHouse = { day: newState.day, rolled: true, methPriceNoise: 1, weedOfferNoise: 1, methStock: Number(rules[`ken_meth_stock_${newState.difficulty}`]), weedDemand: Number(rules[`ken_weed_demand_${newState.difficulty}`]), stats: { ...(newState.kenHouse?.stats || {}) }, wifeFlavor: "Ken's wife is yelling about a missing bottle opener." };
      ensureCornerStoreState(newState);
    }
  }

  if (local) {
    if (destinationId === 'valley_west_boulevard') {
      newState.eventRuntime ||= { counts: {}, last_day: {}, flags: [], counters: {} };
      newState.eventRuntime.counters ||= {};
      newState.eventRuntime.counters.localIncidentRollDay ||= {};
      const lastRoll = Number(newState.eventRuntime.counters.localIncidentRollDay[destinationId] ?? -10000);
      if (Number(newState.day) - lastRoll >= Number(destination.localIncidentCooldownDays || 1)) {
        newState.eventRuntime.counters.localIncidentRollDay[destinationId] = Number(newState.day);
        if (random() < Number(destination.dangerLevel || 0)) newState.activeEvent = { type: 'mugging' };
        else if (random() < Number(destination.policeLevel || 0)) newState.activeEvent = { type: 'police' };
      }
    }
    return newState;
  }

  if (!destination.isHome && !destination.isPawnShop && destinationId !== 'cavemans_house' && destination.marketShiftEnabled !== false) {
    const eventRand = random();
    if (eventRand < 0.2) {
      const shiftedProducts = PRODUCTS.filter((product) => newState.marketBuyPrices[product.id] != null || newState.marketSellPrices[product.id] != null);
      const totalWeight = shiftedProducts.reduce((total, product) => total + Number(product.marketShiftWeight || 1), 0);
      let target = random() * totalWeight;
      let randomProduct = null;
      for (const product of shiftedProducts) {
        target -= Number(product.marketShiftWeight || 1);
        if (target < 0) { randomProduct = product.id; break; }
      }
      randomProduct ||= shiftedProducts.at(-1)?.id || null;
      if (randomProduct) {
        const isSurge = random() > 0.5;
        if (isSurge) {
          newState.messages.push(`Cops made a huge bust! Prices for ${randomProduct} are sky high!`);
          const multiplier = Math.floor(random() * 3 + 2);
          if (newState.marketBuyPrices[randomProduct] != null) newState.marketBuyPrices[randomProduct] *= multiplier;
          if (newState.marketSellPrices[randomProduct] != null) newState.marketSellPrices[randomProduct] *= multiplier;
          newState.marketPrices[randomProduct] = newState.marketBuyPrices[randomProduct];
        } else {
          newState.messages.push(`Market flooded! Prices for ${randomProduct} have bottomed out!`);
          const divisor = random() * 3 + 2;
          if (newState.marketBuyPrices[randomProduct] != null) newState.marketBuyPrices[randomProduct] = Math.max(1, Math.floor(newState.marketBuyPrices[randomProduct] / divisor));
          if (newState.marketSellPrices[randomProduct] != null) newState.marketSellPrices[randomProduct] = Math.max(1, Math.floor(newState.marketSellPrices[randomProduct] / divisor));
          newState.marketPrices[randomProduct] = newState.marketBuyPrices[randomProduct];
        }
        cache.markets[destinationId] = structuredClone(newState.marketBuyPrices);
        cache.sellMarkets[destinationId] = structuredClone(newState.marketSellPrices);
      }
    }
  }

  if (destinationId === 'home') {
    if (random() < newState.homeRaidChance) newState.activeEvent = { type: 'home_raid' };
    return newState;
  }
  if (destinationId === 'pawn_shop') { rollPawnTravelStock(newState, random); return newState; }

  if (destinationId !== 'cavemans_house' && random() < Math.min(0.9, 0.15 * policy.encounterMultiplier)) {
    const ownedProducts = PRODUCTS.filter((product) => newState.inventory[product.id] > 0);
    if (ownedProducts.length > 0) {
      const productInfo = ownedProducts[Math.floor(random() * ownedProducts.length)];
      const validOptions = productInfo.options.filter((option) => option.weight <= newState.inventory[productInfo.id]);
      if (validOptions.length > 0) {
        const option = validOptions[Math.floor(random() * validOptions.length)];
        const isTrade = random() < 0.4;
        let tradeItem = null;
        let stolen = false;
        if (isTrade) {
          const items = ['stereo', 'computer', 'gun'];
          tradeItem = items[Math.floor(random() * items.length)];
          if (productInfo.id === 'meth' || productInfo.id === 'heroin') stolen = random() < 0.5;
        }
        newState.activeEvent = { type: 'buyer_encounter', product: productInfo.id, amount: option.weight, amountLabel: option.label, isTrade, tradeItem, stolen, buyerInt: random() };
        return newState;
      }
    }
  }

  if (destinationId === 'cavemans_house') {
    const cavemanEvent = random();
    if (cavemanEvent < 0.1) { newState.health -= 15; newState.messages.push('Ouch! A rattlesnake bit you in the yard! Lost 15 health.'); }
    else if (cavemanEvent < 0.15) newState.activeEvent = { type: 'caveman_whore' };
    return newState;
  }

  const diffModifier = newState.difficulty === 'low' ? 0.50 : newState.difficulty === 'medium' ? 0.75 : 1.0;
  const repModifier = ((50 - newState.streetRep) * 0.002);
  const effectiveDanger = Math.min(0.9, ((destination.dangerLevel * diffModifier) + repModifier) * policy.muggingMultiplier * productRisk.mugging * policy.encounterMultiplier);
  const effectivePolice = Math.min(0.9, destination.policeLevel * diffModifier * policy.policeMultiplier * productRisk.police * policy.encounterMultiplier);
  if (random() < effectiveDanger) newState.activeEvent = { type: 'mugging' };
  else if (random() < effectivePolice) {
    newState.activeEvent = { type: 'police' };
    if (useChaz) chaz.stats.policeStops = Number(chaz.stats.policeStops || 0) + 1;
  }
  return newState;
};

// Functions to resolve active events
export const resolveEvent = (currentState, choice, random = Math.random) => {
  let newState = { 
    ...currentState, 
    inventory: { ...currentState.inventory },
    stash: { ...currentState.stash },
    messages: [...currentState.messages],
    activeEvent: null 
  };
  
  const event = currentState.activeEvent;
  
  if (event.type === 'home_raid') {
    let lostProduct = false;
    PRODUCTS.forEach(p => {
      if (newState.stash[p.id] > 0) {
        newState.stash[p.id] = 0;
        lostProduct = true;
      }
    });
    if (lostProduct) {
      newState.messages.push("HOME RAIDED! The cops found your stash and took everything!");
    } else {
      newState.messages.push("Home raided! The cops tossed the place but you had no stash.");
    }
    return newState;
  }

  if (event.type === 'mugging') {
    if (choice === 'submit') {
      newState.streetRep = Math.max(0, newState.streetRep - 5);
      const lostCash = Math.floor(newState.cash * 0.5);
      newState.cash -= lostCash;
      
      PRODUCTS.forEach(p => {
        if (newState.inventory[p.id] > 0) {
           newState.inventory[p.id] = Math.floor(newState.inventory[p.id] * 0.5);
        }
      });
      newState.messages.push(`You submitted. Lost 50% of your stuff. Street rep took a hit (-5).`);
    } else {
      const lostCash = Math.floor(newState.cash * (random() * 0.5 + 0.1));
      newState.cash -= lostCash;
      newState.health -= 15;
      newState.messages.push(`You were violently mugged! Lost $${lostCash} and took 15 damage.`);
    }
    return newState;
  }

  if (event.type === 'police') {
    // 50% chance home raid chance goes up when caught
    if (random() < 0.5) {
      newState.homeRaidChance += 0.01;
      newState.messages.push("Cops logged your info. Home heat increased.");
    }

    if (choice === 'bribe') {
      // 50% chance bribe works
      if (random() < 0.5) {
        const bribeAmt = Math.floor(newState.cash * 0.3);
        newState.cash -= bribeAmt;
        newState.messages.push(`Bribe successful! You paid the dirty cops $${bribeAmt} and kept your stash.`);
      } else {
        newState.health -= 20;
        newState.cash = 0;
        PRODUCTS.forEach(p => { newState.inventory[p.id] = 0; });
        newState.messages.push(`Bribe FAILED! The cops beat you up (-20 hp), took all your cash and product.`);
      }
    } else {
      // Accept fate
      let lostProduct = false;
      PRODUCTS.forEach(p => {
        if (newState.inventory[p.id] > 0) {
          newState.inventory[p.id] = 0;
          lostProduct = true;
        }
      });
      if (lostProduct) {
        newState.messages.push(`POLICE BUST! All product on your person was confiscated!`);
      } else {
        newState.streetRep = Math.max(0, newState.streetRep - 1);
        newState.messages.push(`Police patted you down but you were clean. They harassed you and made you look weak. Street Rep -1.`);
      }
    }
    return newState;
  }

  if (event.type === 'buyer_encounter') {
    const { product, amount, isTrade, tradeItem, stolen, buyerInt } = event;
    const sellPrices = currentState.marketSellPrices && Object.keys(currentState.marketSellPrices).length ? currentState.marketSellPrices : currentState.marketPrices;
    const price = sellPrices?.[product] || PRODUCTS.find(p => p.id===product).baseMax;
    const cashValue = price * amount;
    
    let actualGiven = amount;
    let repChange = 0;
    
    if (choice === 'short') {
      actualGiven = Math.max(1, Math.floor(amount * 0.95)); // short by 5%
      if (buyerInt > 0.5) { // caught!
        newState.streetRep = Math.max(0, Math.min(100, newState.streetRep - 5));
        newState.messages.push(`Buyer caught you shorting them! Trade cancelled. Street Rep -5.`);
        return newState; 
      }
    } else if (choice === 'extra') {
      actualGiven = Math.min(newState.inventory[product], Math.ceil(amount * 1.05));
      repChange = +2;
    }
    
    newState.inventory[product] -= actualGiven;
    newState.streetRep = Math.max(0, Math.min(100, newState.streetRep + repChange));
    
    if (isTrade) {
       const finalItemKey = stolen ? `stolen_${tradeItem}` : tradeItem;
       newState.barterInventory[finalItemKey] += 1;
       newState.messages.push(`Traded ${formatProductQuantity(product, actualGiven)} of ${PRODUCTS.find((item) => item.id === product)?.name || product} for a ${stolen ? 'Stolen ' : ''}${tradeItem}.`);
    } else {
       newState.cash += cashValue;
       newState.messages.push(`Sold ${formatProductQuantity(product, actualGiven)} of ${PRODUCTS.find((item) => item.id === product)?.name || product} to a random buyer for $${cashValue}.`);
    }
    return newState;
  }

  if (event.type === 'caveman_whore') {
    if (choice === 'submit') {
      newState.streetRep = Math.max(0, newState.streetRep - 5);
      newState.messages.push("You submitted to the threesome... You feel disgusted. Street rep took a hit (-5).");
    } else {
      newState.health -= 5;
      newState.messages.push("You rejected the threesome. The whore threw a bottle at you. Lost 5 health.");
    }
    return newState;
  }

  return newState;
};


export const getDiscount = (weight) => {
  if (weight >= 448) return 0.7;
  if (weight >= 112) return 0.8;
  if (weight >= 28) return 0.9;
  return 1.0;
};

export const getSellDiscount = (state, weight) => {
  const mode = state?.gameMode || {};
  const debtPhase = (mode.id || state?.gameModeId) === 'long_haul_120'
    && Number(mode.version || 1) >= 3
    && mode.debtClearedDay == null
    && Number(state?.day || 1) <= Number(mode.debtDeadline || 30);
  if (!debtPhase) return getDiscount(weight);
  if (weight >= 448) return 0.85;
  if (weight >= 112) return 0.90;
  if (weight >= 28) return 0.95;
  return 1.0;
};

export const createInitialState = (random = Math.random) => {
  const state = structuredClone(INITIAL_STATE);
  // Offline Classic is deliberately fixed to the original high-difficulty baseline.
  state.difficulty = 'high';
  const market = generateMarket(state.currentLocationId, random);
  state.marketPrices = structuredClone(market.buyPrices);
  state.marketBuyPrices = structuredClone(market.buyPrices);
  state.marketSellPrices = structuredClone(market.sellPrices);
  state.marketStock = structuredClone(market.stock);
  state.marketDemand = structuredClone(market.demand);
  state.marketProfile = { locationId: state.currentLocationId, buyProducts: [], sellProducts: [], limitedStock: false, limitedDemand: false, volatilityEvents: structuredClone(market.events || {}) };
  state.dailyLocationMarkets = { day: state.day, productCatalogVersion: PRODUCT_CATALOG_VERSION, markets: { [state.currentLocationId]: structuredClone(market.buyPrices) }, sellMarkets: { [state.currentLocationId]: structuredClone(market.sellPrices) }, stocks: { [state.currentLocationId]: structuredClone(market.stock) }, demands: { [state.currentLocationId]: structuredClone(market.demand) }, streetPrices: {}, events: { [state.currentLocationId]: structuredClone(market.events || {}) } };
  state.streetPrices = generateStreetPrices(state.currentLocationId, state, state.day);
  return ensureCornerStoreState(state);
};

const inventoryCount = (state) => inventoryCapacityUsed(state.inventory);

const carriedProductRisk = (state) => {
  let police = 1;
  let mugging = 1;
  const tiers = [];
  PRODUCTS.forEach((product) => {
    const quantity = Number(state.inventory?.[product.id] || 0);
    let selected = null;
    (product.riskTiers || []).forEach((tier) => { if (quantity >= Number(tier.minimum || 0)) selected = tier; });
    if (!selected) return;
    const tierPolice = Math.max(1, Number(selected.police || 1));
    const tierMugging = Math.max(1, Number(selected.mugging || 1));
    police = Math.max(police, tierPolice);
    mugging = Math.max(mugging, tierMugging);
    tiers.push({ productId: product.id, quantity, policeMultiplier: tierPolice, muggingMultiplier: tierMugging });
  });
  return { police, mugging, tiers };
};

export const applyClassicAction = (currentState, action, payload = {}, random = Math.random) => {
  if (currentState.day > currentState.maxDays || currentState.health <= 0) {
    throw new Error('This Classic run has already ended.');
  }

  if (action === 'travel') return handleTravel(payload.location_id, currentState, random, payload.travel_mode || 'normal');
  if (action === 'resolve_event') return resolveEvent(currentState, payload.choice, random);

  const state = ensureCornerStoreState(structuredClone(currentState));
  const location = LOCATIONS.find(item => item.id === state.currentLocationId);

  if (action === 'set_difficulty') {
    throw new Error('Difficulty is controlled by the server administrator.');
  }

  if (action === 'corner_store_leave') {
    if (state.currentLocationId !== 'corner_store') throw new Error('That action is only available at The Corner Store.');
    state.currentLocationId = 'trailer_park';
    state.messages = ['Left The Corner Store before the parking lot could invent a new emergency.', ...state.messages];
    return state;
  }

  if (action === 'corner_store_open_board') {
    if (state.currentLocationId !== 'corner_store') throw new Error('That action is only available at The Corner Store.');
    state.bulletinBoard = bulletinBoard(state.day);
    state.cornerStore.stats.boardViews += 1;
    state.messages = ['Checked The Corner Store bulletin board. Most of the useful sections are still coming soon.', ...state.messages];
    return state;
  }

  if (action === 'drink_beer') {
    if (Number(state.streetInventory.beer || 0) <= 0) throw new Error('You do not have a beer to drink.');
    state.streetInventory.beer -= 1;
    const roll = random();
    if (roll < 0.55) {
      const before = state.health;
      state.health = Math.min(100, state.health + 2);
      state.messages = [state.health > before ? 'Drank a cold beer. It took the edge off and restored 2 health.' : 'Drank a cold beer. You were already feeling as good as this neighborhood allows.', ...state.messages];
    } else if (roll < 0.85) state.messages = ['Drank a beer. Nothing improved, but the can did make a satisfying crunch.', ...state.messages];
    else { state.health = Math.max(0, state.health - 2); state.messages = ['That beer tasted like warm pennies. Lost 2 health and some faith in refrigeration.', ...state.messages]; }
    state.cornerStore.stats.beersDrank += 1;
    if (!state.eventRuntime.flags.includes('drank_beer')) state.eventRuntime.flags.push('drank_beer');
    return state;
  }

  if (action === 'corner_store_buy_smokes' || action === 'corner_store_buy_beer') {
    if (state.currentLocationId !== 'corner_store') throw new Error('That action is only available at The Corner Store.');
    const quantity = positiveQuantity(payload.quantity, 100);
    const rules = storeRules(state);
    const smokes = action === 'corner_store_buy_smokes';
    const itemId = smokes ? 'smokes' : 'beer';
    const counterKey = smokes ? 'smokesBought' : 'beerBought';
    const priceKey = smokes ? 'corner_store_smokes_price' : 'corner_store_beer_price';
    const limitKey = smokes ? 'corner_store_smokes_daily_limit' : 'corner_store_beer_daily_limit';
    const limit = Number(rules[limitKey]);
    if (Number(state.cornerStore[counterKey]) + quantity > limit) {
      throw new Error(`Only ${Math.max(0, limit - Number(state.cornerStore[counterKey]))} more available today.`);
    }
    const total = Number(rules[priceKey]) * quantity;
    if (Number(state.cash) < total) throw new Error('You do not have enough cash.');
    state.cash -= total;
    state.streetInventory[itemId] += quantity;
    state.cornerStore[counterKey] += quantity;
    state.cornerStore.stats[smokes ? 'smokesPurchased' : 'beerPurchased'] += quantity;
    const label = itemId === 'smokes' ? `pack${quantity === 1 ? '' : 's'} of smokes` : `beer${quantity === 1 ? '' : 's'}`;
    state.messages = [`Bought ${quantity} ${label} for $${total} at The Corner Store.`, ...state.messages];
    return state;
  }

  if (action === 'corner_store_buy_scratchers') {
    if (state.currentLocationId !== 'corner_store') throw new Error('That action is only available at The Corner Store.');
    const quantity = positiveQuantity(payload.quantity, 20);
    const rules = storeRules(state);
    const limit = Number(rules.corner_store_scratcher_daily_limit);
    if (Number(state.cornerStore.scratchersBought) + quantity > limit) {
      throw new Error(`Only ${Math.max(0, limit - Number(state.cornerStore.scratchersBought))} more scratchers available today.`);
    }
    const cost = Number(rules.corner_store_scratcher_price) * quantity;
    if (Number(state.cash) < cost) throw new Error('You do not have enough cash.');
    const results = Array.from({ length: quantity }, () => rollScratcher(random));
    const payout = results.reduce((total, value) => total + value, 0);
    state.cash = state.cash - cost + payout;
    state.cornerStore.scratchersBought += quantity;
    state.cornerStore.lastScratcherResults = results;
    state.cornerStore.lastScratcherReveals = results.map(scratcherReveal);
    state.cornerStore.lastScratcherCost = cost;
    state.cornerStore.lastScratcherPayout = payout;
    state.cornerStore.scratcherHistory.push({ day: Number(state.day), results, cost, payout });
    state.cornerStore.scratcherHistory = state.cornerStore.scratcherHistory.slice(-50);
    state.cornerStore.stats.scratchersPurchased += quantity;
    state.cornerStore.stats.scratcherSpent += cost;
    state.cornerStore.stats.scratcherPaid += payout;
    state.cornerStore.stats.jackpotCount += results.filter((result) => result >= 2000).length;
    state.messages = [payout
      ? `Scratched ${quantity} ticket${quantity === 1 ? '' : 's'}, spent $${cost}, and won $${payout}.`
      : `Scratched ${quantity} ticket${quantity === 1 ? '' : 's'} for $${cost} and won absolutely nothing.`, ...state.messages];
    return state;
  }

  if (action === 'sell_smokes') {
    if (location?.isHome || location?.isPawnShop || location?.isSceneLocation || state.streetPrices?.smokes == null) {
      throw new Error('Nobody is buying smokes at this location.');
    }
    const quantity = positiveQuantity(payload.quantity);
    if (quantity > Number(state.streetInventory.smokes)) throw new Error('You do not have that many packs of smokes.');
    const revenue = quantity * Number(state.streetPrices.smokes);
    state.streetInventory.smokes -= quantity;
    state.cash += revenue;
    state.cornerStore.stats.smokesResold += quantity;
    state.cornerStore.stats.smokesResaleRevenue += revenue;
    state.messages = [`Sold ${quantity} pack${quantity === 1 ? '' : 's'} of smokes for $${revenue}.`, ...state.messages];
    return state;
  }

  if (action === 'ken_sell_bud') {
    if (state.currentLocationId !== 'kens_house') throw new Error("You need to be at Ken's house.");
    const amount = Number(payload.amount || 0);
    if (amount <= 0 || amount > Number(state.inventory.bud) || amount > Number(state.kenHouse.weedDemand)) throw new Error('Ken cannot buy that amount.');
    const total = Math.floor(amount * Number(state.kenHouse.weedOffer));
    state.inventory.bud -= amount; state.cash += total; state.kenHouse.weedDemand -= amount;
    state.messages = [`Sold Ken ${amount}g of bud for $${total}.`, ...state.messages]; return state;
  }
  if (action === 'ken_buy_meth') {
    if (state.currentLocationId !== 'kens_house') throw new Error("You need to be at Ken's house.");
    if (Number(state.streetRep) < Number(state.kenHouse.repRequirement)) throw new Error('Ken does not trust you enough yet.');
    const amount = Number(payload.amount || 0), cost = Math.floor(amount * Number(state.kenHouse.methPrice));
    if (amount <= 0 || amount > Number(state.kenHouse.methStock) || cost > Number(state.cash)) throw new Error('That meth deal is unavailable.');
    state.cash -= cost; state.inventory.meth += amount; state.kenHouse.methStock -= amount;
    state.messages = [`Bought ${amount}g of meth from Ken for $${cost}.`, ...state.messages]; return state;
  }
  if (action === 'ken_trade_junk') {
    if (state.currentLocationId !== 'kens_house') throw new Error("You need to be at Ken's house.");
    const item = String(payload.item_id || '');
    if (!BARTER_PRICES[item] || Number(state.barterInventory[item] || 0) <= 0) throw new Error('You do not have that junk.');
    const value = Math.floor(Number(BARTER_PRICES[item]) * Number(state.kenHouse.junkFactor));
    const amount = Math.min(Number(state.kenHouse.methStock), Math.max(.1, Math.round(value / Number(state.kenHouse.methPrice) * 10) / 10));
    state.barterInventory[item] -= 1; state.inventory.meth += amount; state.kenHouse.methStock -= amount;
    state.messages = [`Traded ${item.replaceAll('_',' ')} to Ken for ${amount}g of meth.`, ...state.messages]; return state;
  }
  if (action === 'ken_look_around') { state.messages = [state.kenHouse.wifeFlavor, ...state.messages]; return state; }
  if (action === 'ken_give_wife_beer') {
    if (Number(state.streetInventory.beer || 0) <= 0) throw new Error('You do not have a beer.');
    state.streetInventory.beer -= 1;
    state.streetRep = Math.min(100, Number(state.streetRep) + 1);
    ensureCornerStoreState(state);
    state.messages = ["Ken's wife takes the beer and stops yelling for almost thirty seconds. Reputation +1.", ...state.messages]; return state;
  }

  if (action === 'buy_travel_gear') {
    if (!location?.isPawnShop) throw new Error('Travel gear can only be bought at the Pawn Shop.');
    const itemId = String(payload.item_id || '').toLowerCase();
    if (!Object.hasOwn(TRAVEL_GEAR, itemId)) throw new Error('That travel item does not exist.');
    const stock = rollPawnTravelStock(state, random);
    if (state.travelGear[itemId].owned) throw new Error(`You already own a ${itemId}.`);
    if (!stock[itemId]?.available) throw new Error(`The Pawn Shop does not have a ${itemId} today.`);
    const price = Number(stock[itemId].price || 0);
    if (state.cash < price) throw new Error('You do not have enough cash.');
    state.cash -= price;
    state.travelGear[itemId] = { owned: true, acquiredDay: Number(state.day), purchasePrice: price };
    stock[itemId].available = false;
    state.messages = [`Bought a ${itemId === 'skateboard' ? 'beat-up skateboard' : 'used bicycle'} for $${price}. Debt grows slower, but trouble sees you coming.`, ...state.messages];
    return state;
  }

  if (action === 'buy') {
    if (location?.isHome || location?.isPawnShop || location?.isSceneLocation) throw new Error('There is no product market at this location.');
    const productId = String(payload.product_id || '');
    const weight = Number(payload.weight);
    const buyPrices = state.marketBuyPrices && Object.keys(state.marketBuyPrices).length ? state.marketBuyPrices : state.marketPrices;
    const price = buyPrices[productId];
    if (price == null || !marketProducts(location, 'buy').includes(productId)) throw new Error('That product is not sold at this location.');
    const stock = state.marketStock?.[productId];
    if (stock != null && Number(stock) < weight) throw new Error(`Only ${formatProductQuantity(productId, Number(stock))} remains in this market today.`);
    const cost = Math.floor(price * weight * getDiscount(weight));
    if (inventoryCount(state) + productCapacityAmount(productId, weight) > state.capacity) throw new Error(`You can only carry ${state.capacity}g of capacity.`);
    if (state.cash < cost) throw new Error('You do not have enough cash.');
    let amountGained = weight;
    if (location?.shortsProducts && productId !== 'bud' && random() < 0.4) amountGained *= 1 - (random() * 0.4 + 0.1);
    state.cash -= cost;
    state.inventory[productId] += amountGained;
    if (stock != null) state.marketStock[productId] = Math.max(0, Number(stock) - weight);
    const cache = state.dailyLocationMarkets;
    if (cache?.stocks?.[state.currentLocationId]) cache.stocks[state.currentLocationId] = structuredClone(state.marketStock);
    return state;
  }

  if (action === 'sell' || action === 'sell_all') {
    if (location?.isHome || location?.isPawnShop || location?.isSceneLocation) throw new Error('There is no product market at this location.');
    const productId = String(payload.product_id || '');
    const sellPrices = state.marketSellPrices && Object.keys(state.marketSellPrices).length ? state.marketSellPrices : state.marketPrices;
    const price = sellPrices[productId];
    if (price == null || !marketProducts(location, 'sell').includes(productId)) throw new Error('That product is not bought at this location.');
    const held = Number(state.inventory[productId] || 0);
    const demand = state.marketDemand?.[productId];
    const amount = action === 'sell_all' ? (demand == null ? held : Math.min(held, Number(demand))) : Number(payload.weight);
    if (amount <= 0 || held < amount) throw new Error('You do not have enough product.');
    if (demand != null && Number(demand) < amount) throw new Error(`This market will buy only ${formatProductQuantity(productId, Number(demand))} more today.`);
    const revenue = Math.floor(price * amount * getSellDiscount(state, amount));
    state.cash += revenue;
    state.inventory[productId] = Math.max(0, held - amount);
    if (demand != null) state.marketDemand[productId] = Math.max(0, Number(demand) - amount);
    if (action === 'sell_all' && amount < held) state.messages = [`Local demand topped out. Sold ${formatProductQuantity(productId, amount)} and kept the rest.`, ...state.messages];
    const cache = state.dailyLocationMarkets;
    if (cache?.demands?.[state.currentLocationId]) cache.demands[state.currentLocationId] = structuredClone(state.marketDemand);
    return state;
  }

  if (action === 'pay_debt') {
    const amount = Math.min(Number.parseInt(payload.amount, 10), state.cash, state.debt);
    if (!Number.isFinite(amount) || amount <= 0) throw new Error('Enter a valid payment amount.');
    state.cash -= amount;
    state.debt -= amount;
    state.messages = [`Paid $${amount} towards debt.`, ...state.messages];
    return state;
  }

  if (action === 'borrow') {
    const amount = Number.parseInt(payload.amount, 10);
    if (!Number.isFinite(amount) || amount <= 0) throw new Error('Enter a valid loan amount.');
    const debtAdded = Math.floor(amount * 1.5);
    state.cash += amount;
    state.debt += debtAdded;
    state.messages = [`Borrowed $${amount}. Your debt increased by $${debtAdded}.`, ...state.messages];
    return state;
  }

  if (action === 'bank') {
    const amount = Number.parseInt(payload.amount, 10);
    if (payload.direction === 'deposit' && state.cash >= amount) {
      state.cash -= amount;
      state.bankCash += amount;
    } else if (payload.direction === 'withdraw' && state.bankCash >= amount) {
      state.cash += amount;
      state.bankCash -= amount;
    }
    return state;
  }

  if (action === 'stash' || action === 'stash_all') {
    const productId = payload.product_id;
    let amount = Number(payload.weight);
    if (action === 'stash_all') {
      amount = payload.direction === 'deposit'
        ? state.inventory[productId]
        : Math.min(state.stash[productId], productQuantityForCapacity(productId, state.capacity - inventoryCount(state)));
    }
    if (payload.direction === 'deposit' && state.inventory[productId] >= amount && amount > 0) {
      state.inventory[productId] -= amount;
      state.stash[productId] += amount;
    } else if (payload.direction === 'withdraw' && state.stash[productId] >= amount && inventoryCount(state) + productCapacityAmount(productId, amount) <= state.capacity && amount > 0) {
      state.stash[productId] -= amount;
      state.inventory[productId] += amount;
    }
    return state;
  }

  if (action === 'sell_barter') {
    const itemKey = payload.item_id;
    if (state.barterInventory[itemKey] <= 0) return state;
    let cashGained = BARTER_PRICES[itemKey];
    let message = `Sold ${itemKey.replace('_', ' ')} to the Pawn Shop for $${cashGained}.`;
    let bust = false;
    if (itemKey.startsWith('stolen')) {
      const outcome = random();
      if (outcome < 0.05) {
        cashGained = Math.floor(cashGained * 0.5);
        message = `Pawn shop owner noticed it was hot! Lowballed you for $${cashGained}.`;
      } else if (outcome < 0.10) {
        bust = true;
        cashGained = 0;
        message = 'Pawn shop owner called the cops! You were busted! Lost all product!';
      } else if (outcome < 0.15) {
        cashGained = 0;
        message = 'Pawn shop owner took your hot shit and kicked you out!';
      }
    }
    state.barterInventory[itemKey] -= 1;
    if (bust) state.inventory = { bud: 0, dabs: 0, shrooms: 0, meth: 0, heroin: 0 };
    state.cash += cashGained;
    state.messages = [message, ...state.messages];
    return state;
  }

  throw new Error(`Unknown Classic action: ${action}`);
};
